/**
 * Canvas module — redesigned (iter 10) for breathing room.
 *
 * - Default "list" view: 9 full-width cards stacked vertically with large
 *   textareas — solves the cramped 5-column grid problem.
 *   Cards are grouped into 3 lanes (Infraestructura · Oferta+Cliente · Modelo financiero)
 *   visually separated by an accented section header. No truncation.
 * - "Osterwalder" view (toggle): the classic 5-col grid kept as a visual
 *   reference / printable mode (still editable in textareas).
 *
 * Every block remains a controlled textarea. Save behaviour is unchanged so
 * downstream consumers (PDF export, AI assistants, executive summary) keep
 * working without modification.
 */
import { useEffect, useState } from "react";
import {
  LayoutGrid,
  Rows,
  Handshake,
  Cog,
  Box,
  Sparkles,
  HeartHandshake,
  Megaphone,
  Users,
  Receipt,
  Coins,
} from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import {
  SectionCard,
  PrimaryButton,
  AIBadge,
  Overline,
  SubTabs,
} from "@/components/ui-kit";
import AIAssistant from "@/components/AIAssistant";

const empty = {
  key_partners: "",
  key_activities: "",
  key_resources: "",
  value_propositions: "",
  customer_relationships: "",
  channels: "",
  customer_segments: "",
  cost_structure: "",
  revenue_streams: "",
};

const LANES = [
  {
    key: "infra",
    label_es: "Infraestructura",
    label_en: "Infrastructure",
    accent: "#3A5A6B",
    blocks: ["key_partners", "key_activities", "key_resources"],
  },
  {
    key: "offer",
    label_es: "Oferta + Cliente",
    label_en: "Offer + Customer",
    accent: "#B89154",
    blocks: ["value_propositions", "customer_relationships", "channels", "customer_segments"],
  },
  {
    key: "finance",
    label_es: "Modelo financiero",
    label_en: "Financial model",
    accent: "#9E2A2B",
    blocks: ["cost_structure", "revenue_streams"],
  },
];

const BLOCK_META = {
  key_partners:           { icon: Handshake,      accent: "#3A5A6B" },
  key_activities:         { icon: Cog,            accent: "#3A5A6B" },
  key_resources:          { icon: Box,            accent: "#3A5A6B" },
  value_propositions:     { icon: Sparkles,       accent: "#B89154", featured: true },
  customer_relationships: { icon: HeartHandshake, accent: "#2563EB" },
  channels:               { icon: Megaphone,      accent: "#2563EB" },
  customer_segments:      { icon: Users,          accent: "#2563EB" },
  cost_structure:         { icon: Receipt,        accent: "#9E2A2B" },
  revenue_streams:        { icon: Coins,          accent: "#2563EB" },
};

function BlockCard({ id, label, hint, value, onChange, featured, accent, IconCmp, rows = 6 }) {
  return (
    <div
      className={`group relative bg-white border rounded-sm transition-shadow hover:shadow-sm ${featured ? "border-l-4" : "border-l-2"}`}
      style={{ borderColor: "#E8E6E1", borderLeftColor: accent }}
      data-testid={`canvas-block-${id}`}
    >
      <div className="px-6 py-4 border-b border-[#F0EFE9] flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}14`, color: accent }}>
            <IconCmp className="h-4 w-4" strokeWidth={1.5} />
          </span>
          <div className="min-w-0">
            <Overline><span style={{ color: accent }}>{label}</span></Overline>
            <p className="text-[11px] text-[#8A8886] mt-0.5 truncate">{hint}</p>
          </div>
        </div>
        {featured && (
          <span className="text-[10px] uppercase tracking-[0.16em] text-[#B89154] border border-[#B89154]/30 bg-[#F5EFE6] rounded-sm px-2 py-0.5 shrink-0">
            {/* "Core" badge for the value-prop block */}
            CORE
          </span>
        )}
      </div>
      <div className="px-6 py-4">
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={hint}
          className="w-full bg-transparent text-sm leading-relaxed text-[#1A1A1A] placeholder:text-[#8A8886]/70 resize-y outline-none focus:bg-[#F5F4F0]/40 rounded-sm transition-colors p-2 -m-2 min-h-[120px]"
          data-testid={`canvas-${id}-textarea`}
        />
      </div>
    </div>
  );
}

function OsterwalderGrid({ v, set, t }) {
  // Compact 5-col Osterwalder reference grid. Textareas are still editable but
  // condensed; for serious editing, switch back to list view.
  function compact(id) {
    const meta = BLOCK_META[id];
    return (
      <div className="bg-white border border-[#E8E6E1] rounded-sm p-3 flex flex-col h-full" data-testid={`canvas-compact-${id}`}>
        <Overline><span style={{ color: meta.accent }}>{t(`canvas.${id}`)}</span></Overline>
        <textarea
          value={v[id] || ""}
          onChange={(e) => set(id, e.target.value)}
          rows={5}
          className="mt-2 flex-1 w-full bg-transparent text-xs leading-relaxed text-[#1A1A1A] resize-none outline-none focus:bg-[#F5F4F0]/40 rounded-sm transition-colors p-1 -m-1 min-h-[140px]"
        />
      </div>
    );
  }
  return (
    <div className="grid grid-cols-10 grid-rows-[minmax(220px,1fr)_minmax(220px,1fr)_minmax(180px,auto)] gap-3">
      <div className="col-span-2 row-span-2">{compact("key_partners")}</div>
      <div className="col-span-2">{compact("key_activities")}</div>
      <div className="col-span-2 row-span-2">{compact("value_propositions")}</div>
      <div className="col-span-2">{compact("customer_relationships")}</div>
      <div className="col-span-2 row-span-2">{compact("customer_segments")}</div>
      <div className="col-span-2">{compact("key_resources")}</div>
      <div className="col-span-2">{compact("channels")}</div>
      <div className="col-span-5">{compact("cost_structure")}</div>
      <div className="col-span-5">{compact("revenue_streams")}</div>
    </div>
  );
}

export default function CanvasModule({ data, project, onSave }) {
  const { t, lang } = useLang();
  const [v, setV] = useState({ ...empty, ...(data || {}) });
  const [view, setView] = useState("list"); // "list" | "grid"
  useEffect(() => {
    setV((prev) => {
    const next = { ...empty, ...(data || {}) };
    try { if (JSON.stringify(prev) === JSON.stringify(next)) return prev; } catch (e) { void e; }
    return next;
  });
  }, [data]);
  useEffect(() => {
    onSave?.(v);
  }, [v, onSave]);
  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));

  const ctx = { project: project?.name, value_propositions: v.value_propositions, customer_segments: v.customer_segments };
  const suggestions = lang === "es"
    ? [
        "Rellena el Canvas completo para este negocio (los 9 bloques)",
        "Propón 3 segmentos de clientes prioritarios",
        "Sugiere 5 propuestas de valor diferenciadas",
        "Lista los socios clave críticos para arrancar",
        "Define los canales B2B y B2C óptimos",
      ]
    : [
        "Fill the entire canvas for this business (all 9 blocks)",
        "Propose 3 priority customer segments",
        "Suggest 5 differentiated value propositions",
        "List the key partners critical to launch",
        "Define optimal B2B and B2C channels",
      ];

  return (
    <>
      <SectionCard
        title={t("module.canvas")}
        subtitle={t("canvas.subtitle")}
        action={
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 border border-[#E8E6E1] bg-white rounded-sm p-1" data-testid="canvas-view-toggle">
              <button
                type="button"
                onClick={() => setView("list")}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-sm text-xs ${view === "list" ? "bg-[#2563EB] text-white" : "text-[#5C5B5A] hover:text-[#1A1A1A]"}`}
                data-testid="canvas-view-list-btn"
              >
                <Rows className="h-3.5 w-3.5" strokeWidth={1.5} />
                {lang === "es" ? "Lista" : "List"}
              </button>
              <button
                type="button"
                onClick={() => setView("grid")}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-sm text-xs ${view === "grid" ? "bg-[#2563EB] text-white" : "text-[#5C5B5A] hover:text-[#1A1A1A]"}`}
                data-testid="canvas-view-grid-btn"
              >
                <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.5} />
                {lang === "es" ? "Canvas" : "Canvas"}
              </button>
            </div>
            <AIBadge />
            <AIAssistant module="canvas" context={ctx} suggestions={suggestions} />
          </div>
        }
        testId="canvas-section"
      >
        {view === "list" && (
          <div className="space-y-8" data-testid="canvas-list-view">
            {LANES.map((lane) => (
              <div key={lane.key} data-testid={`canvas-lane-${lane.key}`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-1 h-6 rounded-sm" style={{ backgroundColor: lane.accent }} />
                  <Overline>
                    <span style={{ color: lane.accent }}>{lang === "es" ? lane.label_es : lane.label_en}</span>
                  </Overline>
                </div>
                <div className="grid gap-4">
                  {lane.blocks.map((bid) => {
                    const meta = BLOCK_META[bid];
                    return (
                      <BlockCard
                        key={bid}
                        id={bid}
                        label={t(`canvas.${bid}`)}
                        hint={t(`canvas.hint.${bid}`)}
                        value={v[bid]}
                        onChange={(x) => set(bid, x)}
                        featured={meta.featured}
                        accent={meta.accent}
                        IconCmp={meta.icon}
                        rows={meta.featured ? 8 : 6}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {view === "grid" && (
          <div data-testid="canvas-grid-view">
            <p className="text-xs text-[#8A8886] mb-4">
              {lang === "es"
                ? "Vista clásica de Osterwalder. Recomendado para imprimir / revisar de un vistazo. Para editar cómodamente, cambia a Lista."
                : "Classic Osterwalder view. Best for printing / quick review. For comfortable editing, switch to List."}
            </p>
            <OsterwalderGrid v={v} set={set} t={t} />
          </div>
        )}
      </SectionCard>

    </>
  );
}
